import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Button,
  Box,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import {
  CloseIcon,
  MonetizationOnIcon,
  CheckCircleIcon,
  SendIcon,
} from "../../../components/mui/index.js";
import { useSingleInvoiceActions } from "./actions/single-action.component.js";
import { getQBMailerInvoice } from "../../../shared/billing.api.js";
import {
  renderInvoiceStatuses,
  buildTooltipSummary,
} from "./helpers/tooltips.component.js";

export const QBMailerDetailModal = ({
  accountId,
  open,
  selectedMonth,
  onClose,
  snackbarRef,
  metaData,
}) => {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailData, setDetailData] = useState(null);
  const [refreshToggle, setRefreshToggle] = useState(false);

  const single = useSingleInvoiceActions({
    snackbarRef,
    setIsLoading: setLoading,
  });

  // Fetch fresh detail data when modal opens or refresh is triggered
  const fetchDetailData = () => {
    if (!accountId) {
      console.log(
        "QBMailerDetailModal: No accountId provided, skipping fetchDetailData"
      );
      return;
    }

    setLoading(true);

    getQBMailerInvoice({
      account_id: accountId,
      month: dayjs(selectedMonth).format("YYYY-MM"),
    })
      .then((res) => {
        setDetailData(res?.data || null);
      })
      .catch((e) => {
        snackbarRef.current?.addSnackbar({
          type: "error",
          message: e.message || "Error loading invoice details",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      fetchDetailData();
    }
  }, [open, accountId]);

  // Refresh modal after actions
  const handleRecentlyUpdatedInvoices = () => {
    fetchDetailData();
    //tell parent to refresh list when modal closes
    setRefreshToggle(true);
  };

  useEffect(() => {
    if (Array.isArray(detailData?.invoices) && detailData.invoices.length > 0) {
      setSelectedInvoiceId(detailData.invoices[0].Id);
    } else if (detailData?.invoice?.Id) {
      setSelectedInvoiceId(detailData.invoice.Id);
    } else {
      setSelectedInvoiceId(null);
    }
  }, [detailData, open]);

  // Helper to get invoice by ID
  function getInvoiceById(row, invoiceId) {
    if (!row) return null;
    if (Array.isArray(row.invoices) && invoiceId) {
      return (
        row.invoices.find((inv) => inv.Id === invoiceId) || row.invoices[0]
      );
    }
    return row.invoice || null;
  }

  const invoice = getInvoiceById(detailData, selectedInvoiceId);

  const handleClose = () => {
    // Reset state on close
    setDetailData(null);
    setSelectedInvoiceId(null);
    onClose(refreshToggle);
  };

  return (
    <Dialog
      open={open}
      onClose={(e, r) => {
        e.stopPropagation();
        if (r === "backdropClick") return;
        handleClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Mailer Invoice Details</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {Array.isArray(detailData?.invoices) &&
            detailData.invoices.length > 1 && (
              <Box>
                <FormControl fullWidth>
                  <InputLabel id="select-invoice-label">
                    Select Invoice
                  </InputLabel>
                  <Select
                    labelId="select-invoice-label"
                    value={selectedInvoiceId ?? detailData.invoices[0]?.Id}
                    label="Select Invoice"
                    onChange={(e) => setSelectedInvoiceId(e.target.value)}
                  >
                    {detailData.invoices.map((inv) => (
                      <MenuItem key={inv.Id} value={inv.Id}>
                        {inv.DocNumber
                          ? `Doc #${inv.DocNumber} (${inv.Id})`
                          : `Invoice ID ${inv.Id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h6">
                QB Doc Number: {invoice?.DocNumber ?? "--"}
              </Typography>
              <Typography variant="h6">
                QB Invoice ID: {invoice?.Id ?? "--"}
              </Typography>
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={4}
            flexWrap="wrap"
            alignItems="flex-start"
            sx={{ justifyContent: "space-between" }}
          >
            {/* Left column: Invoice fields */}
            <Box sx={{ maxWidth: 340, flex: 1, minWidth: 0 }}>
              <Typography>
                <strong>Company:</strong>{" "}
                {invoice?.CustomerRef?.name ??
                  detailData?.account_account_name ??
                  "--"}
              </Typography>
              <Typography>
                <strong>Linked Account:</strong>{" "}
                {detailData?.account_account_name ?? "--"}
              </Typography>
              <Typography>
                <strong>Account ID:</strong> {detailData?.account_id ?? "--"}
              </Typography>
              <Typography>
                <strong>Created Date:</strong>{" "}
                {invoice?.MetaData?.CreateTime
                  ? dayjs(invoice.MetaData.CreateTime).format("MMM D, YYYY")
                  : "--"}
              </Typography>
              <Typography>
                <strong>Invoice Date:</strong>{" "}
                {invoice?.TxnDate
                  ? dayjs(invoice.TxnDate).format("MMM D, YYYY")
                  : "--"}
              </Typography>
              <Typography>
                <strong>Status:</strong> {invoice?.EmailStatus ?? "--"}
              </Typography>
              <Typography>
                <strong>Total Amount:</strong>{" "}
                {typeof invoice?.TotalAmt === "number"
                  ? `$${invoice.TotalAmt.toFixed(2)}`
                  : "--"}
              </Typography>
              <Typography>
                <strong>Mailer Overage:</strong>{" "}
                {typeof detailData?.overage_usage === "number"
                  ? detailData.overage_usage
                  : "--"}
              </Typography>
              <Typography>
                <strong>Overage Per Unit Cost:</strong>{" "}
                {typeof detailData?.overage_per_unit === "number"
                  ? `$${detailData.overage_per_unit.toFixed(2)}`
                  : "--"}
              </Typography>
              <Typography>
                <strong>Balance:</strong>{" "}
                {typeof invoice?.Balance === "number"
                  ? `$${invoice.Balance.toFixed(2)}`
                  : "--"}
              </Typography>
            </Box>
            {/* Right column: Action buttons */}
            <Box sx={{ maxWidth: 340, flex: 1, minWidth: 0 }}>
              <Stack direction="column" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: "100%",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Actions
                  </Typography>
                  <Stack direction="column" spacing={2} alignItems="flex-start">
                    {/* Make Invoice */}
                    <Tooltip
                      placement="right"
                      title={
                        <Typography>
                          Create a new invoice for this account
                        </Typography>
                      }
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          width: "100%",
                          minWidth: 180,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();

                          single.openCreateInvoiceAndOveragesModal(
                            { ...detailData, selectedMonth },
                            (result) => {
                              if (result) {
                                handleRecentlyUpdatedInvoices();
                              }
                            }
                          );
                        }}
                        tabIndex={0}
                        role="button"
                      >
                        <MonetizationOnIcon />
                        <Typography>Create Invoice</Typography>
                      </Stack>
                    </Tooltip>
                    {/* Charge Stripe Customer */}
                    <Tooltip
                      placement="right"
                      title={
                        <Typography>
                          {!detailData?.account_id
                            ? `Please link an account before charging customer`
                            : !detailData?.contract_third_party_info
                                ?.stripe_customer_id
                            ? `No Stripe Customer ID found. Please ensure the linked account has a valid Stripe Customer ID before charging.`
                            : "Charge Stripe Customer"}
                        </Typography>
                      }
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{
                          cursor: "pointer",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          "&:hover": { bgcolor: "action.hover" },
                          width: "100%",
                          minWidth: 180,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!detailData?.account_id) {
                            snackbarRef.current?.addSnackbar({
                              type: "error",
                              message: `Please link an account before charging customer`,
                            });
                            return;
                          }
                          if (
                            !detailData?.contract_third_party_info
                              ?.stripe_customer_id
                          ) {
                            snackbarRef.current?.addSnackbar({
                              type: "error",
                              message: `No Stripe Customer ID found. Please ensure the linked account has a valid Stripe Customer ID before charging.`,
                            });
                            return;
                          }
                          single.openChargeCustomerModal(
                            { ...detailData, invoice },
                            (result) => {
                              if (result) {
                                handleRecentlyUpdatedInvoices();
                              }
                            }
                          );
                        }}
                        tabIndex={0}
                        role="button"
                      >
                        <MonetizationOnIcon />
                        <Typography>Charge Stripe Customer</Typography>
                      </Stack>
                    </Tooltip>
                    {/* Mark as Paid */}
                    <Tooltip
                      placement="right"
                      title={<Typography>Mark as Paid</Typography>}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{
                          cursor: "pointer",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          "&:hover": { bgcolor: "action.hover" },
                          width: "100%",
                          minWidth: 180,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          single.openMarkPaidModal(
                            { ...detailData, invoice },
                            (result) => {
                              if (result) {
                                handleRecentlyUpdatedInvoices();
                              }
                            }
                          );
                        }}
                        tabIndex={0}
                        role="button"
                      >
                        <CheckCircleIcon />
                        <Typography>Mark as Paid</Typography>
                      </Stack>
                    </Tooltip>
                    {/* Send Invoice to Customer */}
                    <Tooltip
                      placement="right"
                      title={
                        <Typography>
                          {!detailData?.account_id
                            ? `Please link an account before sending to customer`
                            : invoice?.EmailStatus === "EmailSent"
                            ? `Invoice already has been sent to the customer. Not sending again.`
                            : "Send Invoice to Customer"}
                        </Typography>
                      }
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{
                          cursor: "pointer",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          "&:hover": { bgcolor: "action.hover" },
                          width: "100%",
                          minWidth: 180,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!detailData?.account_id) {
                            snackbarRef.current?.addSnackbar({
                              type: "error",
                              message: `Please link an account before sending to customer`,
                            });
                            return;
                          }
                          if (invoice?.EmailStatus === "EmailSent") {
                            snackbarRef.current?.addSnackbar({
                              type: "info",
                              message: `Invoice already has been sent to the customer. Not sending again.`,
                            });
                            return;
                          }
                          single.openSendInvoiceModal(
                            { ...detailData, invoice },
                            (result) => {
                              if (result) {
                                handleRecentlyUpdatedInvoices();
                              }
                            }
                          );
                        }}
                        tabIndex={0}
                        role="button"
                      >
                        <SendIcon />
                        <Typography>Send Invoice to Customer</Typography>
                      </Stack>
                    </Tooltip>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Stack>
          <Divider />
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Status of Actions
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                {detailData?.invoices && detailData.invoices.length > 0 ? (
                  <Stack spacing={2} sx={{ py: 1.5 }}>
                    {detailData.invoices.map((invoice) => {
                      const logs =
                        detailData.invoice_logs?.filter(
                          (log) => log.quickbooks_invoice_id === invoice.Id
                        ) || [];
                      return (
                        <Box
                          key={invoice.Id}
                          sx={{
                            mb: 2,
                            p: 1,
                            border: "1px solid #eee",
                            borderRadius: 2,
                          }}
                        >
                          {detailData.invoices.length > 1 && (
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold" }}
                            >
                              Invoice Num: {invoice.DocNumber}
                            </Typography>
                          )}

                          <Box>{buildTooltipSummary(invoice, logs, true)}</Box>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No invoice actions found.
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Divider />
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Line Items
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                <Stack spacing={2}>
                  {invoice?.Line?.length > 0 ? (
                    invoice.Line.map((line, idx) => (
                      <Box
                        key={line.Id || idx}
                        sx={{ borderBottom: "1px solid #eee", pb: 1 }}
                      >
                        <Typography>
                          <strong>Line Number:</strong> {line.LineNum ?? "--"}
                        </Typography>
                        <Typography>
                          <strong>Description:</strong>{" "}
                          {line.Description ?? "--"}
                        </Typography>
                        <Typography>
                          <strong>Amount:</strong>{" "}
                          {typeof line.Amount === "number"
                            ? `$${line.Amount.toFixed(2)}`
                            : "--"}
                        </Typography>
                        <Typography>
                          <strong>Detail Type:</strong>{" "}
                          {line.DetailType ?? "--"}
                        </Typography>
                        {line.SalesItemLineDetail && (
                          <>
                            <Typography>
                              <strong>Item:</strong>{" "}
                              {line.SalesItemLineDetail.ItemRef?.name ?? "--"}
                            </Typography>
                            <Typography>
                              <strong>Qty:</strong>{" "}
                              {line.SalesItemLineDetail.Qty ?? "--"}
                            </Typography>
                            <Typography>
                              <strong>Unit Price:</strong>{" "}
                              {typeof line.SalesItemLineDetail.UnitPrice ===
                              "number"
                                ? `$${line.SalesItemLineDetail.UnitPrice.toFixed(
                                    2
                                  )}`
                                : "--"}
                            </Typography>
                          </>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2">
                      No line items found.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Divider />
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Changelogs
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                <Stack spacing={2}>
                  {Array.isArray(detailData?.invoice_logs) &&
                  detailData.invoice_logs.length > 0 ? (
                    detailData.invoice_logs.map((log, idx) => {
                      // Find the invoice for this log
                      const invoices = detailData?.invoices || [];
                      const multipleInvoices = invoices.length > 1;
                      let docNumber = null;
                      if (multipleInvoices && log.quickbooks_invoice_id) {
                        const inv = invoices.find(
                          (i) => i.Id === log.quickbooks_invoice_id
                        );
                        docNumber = inv?.DocNumber;
                      }
                      return (
                        <Box
                          key={log.id || idx}
                          sx={{ borderBottom: "1px solid #eee", pb: 1 }}
                        >
                          {multipleInvoices && (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              Doc #: {docNumber ?? "--"}
                            </Typography>
                          )}
                          <Typography variant="body2">
                            {log.result_status === "error" ? "❌" : "✅"}{" "}
                            {log.created_when
                              ? dayjs(log.created_when).format(
                                  "MMM D, YYYY h:mma"
                                )
                              : "--"}
                          </Typography>
                          <Typography variant="body2">{log.action}</Typography>
                          <Typography variant="body2">
                            {log.changed_fields
                              ? JSON.stringify(log.changed_fields)
                              : ""}
                          </Typography>
                          <Typography variant="body2">
                            {log.result_message}
                          </Typography>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="error">
                      No changelogs found.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
      {single.modals}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(255,255,255,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Dialog>
  );
};
